var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as AccountService from '../service/account-service.js';
import { Group } from './Group.js';
import { normalizeText } from '../utils.js';
import { ResourceProperty } from './ResourceProperty.js';
/**
 * This class defines an [Account](https://en.wikipedia.org/wiki/Account_(bookkeeping)) of a [[Book]].
 *
 * It maintains a balance of all amount [credited and debited](http://en.wikipedia.org/wiki/Debits_and_credits) in it by [[Transactions]].
 *
 * An Account can be grouped by [[Groups]].
 *
 * @remarks
 * `Account` has no `getBalance()` method. To retrieve account balances, use
 * {@link Book.getBalancesReport} and read the resulting {@link BalancesContainer}.
 *
 * @public
 */
export class Account extends ResourceProperty {
    constructor(book, payload) {
        super(payload || { createdAt: `${Date.now()}` });
        this.book = book;
    }
    /** @internal */
    getConfig() {
        return this.book.getConfig();
    }
    /**
     * Gets the Account internal id.
     *
     * @returns The Account internal id
     */
    getId() {
        return this.payload.id;
    }
    /**
     * Gets the Account name.
     *
     * @returns The Account name
     */
    getName() {
        return this.payload.name;
    }
    /**
     * Sets the name of the Account.
     *
     * @param name - The name to set
     *
     * @returns This Account, for chaining
     */
    setName(name) {
        this.payload.name = name;
        return this;
    }
    /**
     * Tells if the balance of this Account has been verified/audited.
     *
     * @returns True if the balance of this Account has been verified/audited
     */
    isBalanceVerified() {
        return this.payload.balanceVerified;
    }
    /**
     * Gets the normalized name of this Account without spaces or special characters.
     *
     * @returns The name of this Account without spaces or special characters
     */
    getNormalizedName() {
        if (this.payload.normalizedName) {
            return this.payload.normalizedName;
        }
        else {
            return normalizeText(this.getName());
        }
    }
    /**
     * Gets the type of this Account.
     *
     * @returns The [[AccountType]] of this Account
     */
    getType() {
        return this.payload.type;
    }
    /**
     * Sets the type of the Account.
     *
     * @param type - The [[AccountType]] to set
     *
     * @returns This Account, for chaining
     */
    setType(type) {
        this.payload.type = type;
        return this;
    }
    /**
     * Tells if this Account is archived.
     *
     * @returns True if the Account is archived
     */
    isArchived() {
        return this.payload.archived;
    }
    /**
     * Sets Account archived/unarchived.
     *
     * @param archived - True to archive, false to unarchive
     *
     * @returns This Account, for chaining
     */
    setArchived(archived) {
        this.payload.archived = archived;
        return this;
    }
    /**
     * Tells if the Account has any transaction already posted.
     *
     * Accounts with transaction posted, even with zero balance, can only be archived.
     *
     * @returns True if the Account has transactions posted
     */
    hasTransactionPosted() {
        return this.payload.hasTransactionPosted;
    }
    /**
     * Tells if the Account is permanent.
     *
     * Permanent Accounts are the ones which final balance is relevant and keep its balances over time.
     *
     * They are also called [Real Accounts](http://en.wikipedia.org/wiki/Account_(Accountancy)#Based_on_periodicity_of_flow)
     *
     * Usually represents assets or tangibles, capable of being perceived by the senses or the mind, like bank Accounts, money, debts and so on.
     *
     * @returns True if its a permanent Account
     */
    isPermanent() {
        return this.payload.permanent;
    }
    /**
     * Tells if the Account has a Credit nature or Debit otherwise.
     *
     * Credit Accounts are just for representation purposes. It increase or decrease the absolute balance. It doesn't affect the overall balance or the behavior of the system.
     *
     * The absolute balance of credit Accounts increase when it participate as a credit/origin in a transaction. Its usually for Accounts that increase the balance of the assets, like revenue Accounts.
     *
     * ```
     *         Crediting a credit
     *   Thus ---------------------> Account increases its absolute balance
     *         Debiting a debit
     *
     *
     *         Debiting a credit
     *   Thus ---------------------> Account decreases its absolute balance
     *         Crediting a debit
     * ```
     *
     * As a rule of thumb, and for simple understanding, almost all Accounts are Debit nature (NOT credit), except the ones that "offers" amount for the books, like revenue Accounts.
     *
     * @returns True if the Account has credit nature
     */
    isCredit() {
        return this.payload.credit;
    }
    /**
     * Gets the [[Groups]] of this Account.
     *
     * When groups are already embedded in the account payload (e.g. from
     * {@link Bkper.getBook} with includeGroups), resolves them from the
     * book's cache instead of making API calls.
     *
     * @returns Promise with the [[Groups]] of this Account
     */
    getGroups() {
        return __awaiter(this, void 0, void 0, function* () {
            const id = this.getId();
            if (!id) {
                return [];
            }
            //Ensure groups are pre-loaded in the book cache to avoid multiple API calls when resolving groups from the payload
            yield this.book.getGroups();
            // resolve them from the book's group cache to get properly tree-linked Group objects
            if (this.payload.groups != null) {
                const groups = [];
                for (const groupPayload of this.payload.groups) {
                    const group = yield this.book.getGroup(groupPayload.id);
                    if (group) {
                        groups.push(group);
                    }
                }
                return groups;
            }
            return [];
        });
    }
    /**
     * Sets the groups of the Account.
     *
     * @param groups - The groups to set
     *
     * @returns This Account, for chaining
     */
    setGroups(groups) {
        this.payload.groups = undefined;
        if (groups != null) {
            groups.forEach(group => this.addGroup(group));
        }
        return this;
    }
    /**
     * Adds a group to the Account.
     *
     * @param group - The group to add
     *
     * @returns This Account, for chaining
     */
    addGroup(group) {
        if (!this.payload.groups) {
            this.payload.groups = [];
        }
        if (group instanceof Group) {
            this.payload.groups.push(group.json());
        }
        else {
            this.payload.groups.push(group);
        }
        return this;
    }
    /**
     * Removes a group from the Account.
     *
     * @param group - The group name, id or object to remove
     *
     * @returns Promise with this Account, for chaining
     */
    removeGroup(group) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.payload.groups != null) {
                let groupObject = undefined;
                if (group instanceof Group) {
                    groupObject = group;
                }
                else if (typeof group == 'string') {
                    groupObject = yield this.book.getGroup(group);
                }
                if (groupObject) {
                    for (let i = 0; i < this.payload.groups.length; i++) {
                        const groupId = this.payload.groups[i].id;
                        if (groupId == groupObject.getId()) {
                            this.payload.groups.splice(i, 1);
                        }
                    }
                }
            }
            return this;
        });
    }
    /**
     * Tells if this Account is in the [[Group]].
     *
     * @param group - The Group name, id or object
     *
     * @returns Promise with true if the Account is in the group
     */
    isInGroup(group) {
        return __awaiter(this, void 0, void 0, function* () {
            if (group == null) {
                return false;
            }
            //Group object
            if (group instanceof Group) {
                return this.isInGroupObject_(group);
            }
            //id or name
            var foundGroup = yield this.book.getGroup(group);
            if (foundGroup == null) {
                return false;
            }
            return this.isInGroupObject_(foundGroup);
        });
    }
    /** @internal */
    isInGroupObject_(group) {
        const groupId = group.getId();
        if (this.payload.groups == null || !groupId) {
            return false;
        }
        for (var i = 0; i < this.payload.groups.length; i++) {
            if (this.payload.groups[i].id === groupId) {
                return true;
            }
        }
        return false;
    }
    /**
     * Performs create new Account.
     *
     * @returns Promise with this Account after creation
     */
    create() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield AccountService.createAccount(this.book.getId(), this.payload, this.getConfig());
            this.updateAccountCache();
            return this;
        });
    }
    /**
     * Performs update Account, applying pending changes.
     *
     * @returns Promise with this Account after update
     */
    update() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield AccountService.updateAccount(this.book.getId(), this.payload, this.getConfig());
            this.updateAccountCache();
            return this;
        });
    }
    /**
     * Performs delete Account.
     *
     * @returns Promise with this Account after deletion
     */
    remove() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield AccountService.deleteAccount(this.book.getId(), this.payload, this.getConfig());
            this.updateAccountCache(true);
            return this;
        });
    }
    /** @internal */
    updateAccountCache(remove) {
        this.book.setAccount(this.payload, remove);
        this.book.clearCache();
    }
}
//# sourceMappingURL=Account.js.map